import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import equal from "fast-deep-equal";
import { useNavigate } from "react-router";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import Input from "@/components/ui/input";
import Select from "@/components/ui/select";
import Textarea from "@/components/ui/textarea";
import Button from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import IconArrowLeftLane from "@/components/icons/icon-arrow-left-lane";
import type { Review, ReviewFilters, ReviewSource, ReviewType, ReviewContentType, ReviewRatingFilter, ReviewSort } from "@/modules/review/types";
import type { Tag } from "@/modules/tag/types";
import type { Brand, BrandKit } from "@/modules/brand/types";
import type { WallOfLoveTemplate } from "@/modules/wall-of-love-template";
import { TemplateRouter, validateWallOfLoveConfig, shouldFieldBeVisible, shouldGroupBeVisible } from "@/modules/wall-of-love-template";
import { useForms } from "@/modules/form/hooks/use-forms";
import { ValidationErrorsTooltip, type WallValidationError } from "./validation-errors-tooltip";
import { useWallOfLoves } from "../hooks/use-wall-of-loves";
import { WallPlanLimitError } from "../stores/wall-of-love-store";
import { useReviews } from "@/modules/review/hooks/use-reviews";
import { useDirtyState } from "@/modules/form/components/form-builder/hooks/use-dirty-state";
import { useNavigationGuard } from "@/contexts/navigation-guard-context";
import { usePlan, useBrands } from "@/modules/brand";
import type { WallOfLove, WallOfLoveFilters, WallOfLoveSort, WallOfLoveUpdateRequest, WallPreviewRequest, WallPreviewResponse } from "../types";
import WallModeSelector from "./wall-mode-selector";
import ReviewPinnedSelectionSummary from "./review-pinned-selection-summary";
import ReviewSelectionSummary from "./review-selection-summary";
import SelectReviewsModal from "./select-reviews-modal";
import SelectPinnedReviewsModal from "./select-pinned-reviews-modal";
import { StaticReviewSelectionSummary, SelectStaticReviewsModal } from "@/modules/review-selection";
import ModeSwitchConfirmationModal from "./mode-switch-confirmation-modal";
import StatusChangeConfirmationModal from "./status-change-confirmation-modal";
import PublishedLinkModal from "./published-link-modal";
import UpgradeToGrowthModal from "@/components/ui/upgrade-to-growth-modal";
import IconLinkChain from "@/components/icons/icon-link-chain";
import IconAlignTop from "@/components/icons/icon-align-top";
import IconText from "@/components/icons/icon-text";
import IconColorSwatches from "@/components/icons/icon-color-swatches";
import IconDashboardGauge from "@/components/icons/icon-dashboard-gauge";
import IconChatBubbleSquare from "@/components/icons/icon-chat-bubble-square";
import Tooltip from "@/components/ui/tooltip";
import IconSearchCategory from "@/components/icons/icon-search-category";
import { copyToClipboard } from "@/lib/utils";
import { getMockReviews } from "@/modules/review/constants/mock-reviews";

// Debounce delay for preview API calls (in milliseconds)
const PREVIEW_DEBOUNCE_MS = 400;

// Convert WallOfLoveFilters to ReviewFilters for UI display
function convertWallFiltersToReviewFilters(wallFilters: WallOfLoveFilters | null): ReviewFilters {
	const reviewFilters: ReviewFilters = {};

	if (!wallFilters?.groups) return reviewFilters;

	// Convert rating filter (operator: gte, value: number)
	if (wallFilters.groups.rating) {
		const { value, operator } = wallFilters.groups.rating;
		if (operator === "gte" && typeof value === "number") {
			const ratingMap: Record<number, ReviewRatingFilter> = {
				5: "5_and_above",
				4: "4_and_above",
				3: "3_and_above",
				2: "2_and_above",
				1: "1_and_above",
			};
			if (ratingMap[value]) {
				reviewFilters.rating = ratingMap[value];
			}
		}
	}

	// Convert type filter (operator: eq, value: string)
	if (wallFilters.groups.type) {
		const { value, operator } = wallFilters.groups.type;
		if (operator === "eq" && typeof value === "string") {
			reviewFilters.type = value as ReviewType;
		}
	}

	// Convert source filter (operator: in/eq, value: string or comma-separated)
	if (wallFilters.groups.source) {
		const { value, operator } = wallFilters.groups.source;
		if ((operator === "in" || operator === "eq") && typeof value === "string") {
			const sources = value.split(",").map((s) => s.trim()) as ReviewSource[];
			reviewFilters.source = sources.length === 1 ? sources[0] : sources;
		}
	}

	// Convert content_type filter (operator: in/eq, value: string or comma-separated)
	if (wallFilters.groups.content_type) {
		const { value, operator } = wallFilters.groups.content_type;
		if ((operator === "in" || operator === "eq") && typeof value === "string") {
			const types = value.split(",").map((s) => s.trim()) as ReviewContentType[];
			reviewFilters.content_type = types.length === 1 ? types[0] : types;
		}
	}

	// Convert date filter (operator: after/before/between, value: date string)
	if (wallFilters.groups.date) {
		const { value, operator } = wallFilters.groups.date;
		if (typeof value === "string") {
			reviewFilters.date = `${operator}:${value}`;
		}
	}

	// Convert tags filter (operator: in, value: array of tag IDs)
	if (wallFilters.groups.tags) {
		const { value } = wallFilters.groups.tags;
		if (Array.isArray(value)) {
			reviewFilters.tags = value;
		} else if (typeof value === "string") {
			// Handle legacy comma-separated format
			reviewFilters.tags = value.split(",").map((s) => s.trim());
		}
	}

	return reviewFilters;
}

// Convert WallOfLoveSort to ReviewSort
function convertWallSortToReviewSort(wallSort: WallOfLoveSort | null): ReviewSort | undefined {
	if (!wallSort || wallSort.length === 0) return undefined;
	const { field, direction } = wallSort[0];
	// Map field names: WallOfLove uses "date", ReviewSort uses "date" or "created_at"
	const orderByMap: Record<string, ReviewSort["order_by"]> = {
		date: "date",
		rating: "rating",
		content_type: "content_type",
		created_at: "created_at",
	};
	return {
		order_by: orderByMap[field] || "date",
		direction,
	};
}

// Convert numeric font weight to readable label
function getFontWeightLabel(weight: string | number | undefined): string {
	const weightMap: Record<string, string> = {
		"100": "Thin",
		"200": "Extralight",
		"300": "Light",
		"400": "Regular",
		"500": "Medium",
		"600": "Semibold",
		"700": "Bold",
		"800": "Extrabold",
		"900": "Black",
	};
	return weightMap[String(weight)] || "Regular";
}

// Track loaded font weights to avoid duplicate loading
const loadedFontWeights = new Map<string, Set<string>>();

function loadGoogleFont(fontName: string, weights: string[] = ["300", "400", "500", "600", "700", "800"]) {
	// Get or create the set of loaded weights for this font
	if (!loadedFontWeights.has(fontName)) {
		loadedFontWeights.set(fontName, new Set());
	}
	const loadedWeights = loadedFontWeights.get(fontName)!;

	// Filter out weights that are already loaded
	const newWeights = weights.filter((w) => !loadedWeights.has(w));
	if (newWeights.length === 0) return;

	// Mark new weights as loaded
	newWeights.forEach((w) => loadedWeights.add(w));

	// Get all weights to load (combine existing and new)
	const allWeights = Array.from(loadedWeights).sort();

	const linkId = `google-font-${fontName.replace(/\s+/g, "-").toLowerCase()}`;
	const existingLink = document.getElementById(linkId);

	const href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontName)}:wght@${allWeights.join(";")}&display=swap`;

	if (existingLink) {
		// Update existing link with all weights
		(existingLink as HTMLLinkElement).href = href;
	} else {
		// Create new link element
		const link = document.createElement("link");
		link.id = linkId;
		link.rel = "stylesheet";
		link.href = href;
		document.head.appendChild(link);
	}
}

type TabType = "content" | "design";
type WallMode = "dynamic" | "static";

// Resource options cache type
type ResourceOptionsCache = Record<string, Array<{ value: string; label: string }>>;

// Dynamic Select component for fields with options_source
interface DynamicSelectFieldProps {
	fieldKey: string;
	field: import("@/modules/wall-of-love-template").SchemaField;
	value: unknown;
	onChange: (key: string, value: unknown) => void;
	resourceOptions: ResourceOptionsCache;
}

function DynamicSelectField({ fieldKey, field, value, onChange, resourceOptions }: DynamicSelectFieldProps) {
	const fieldValue = value ?? "";
	const showHelpText = !!field.help_text;

	// Get options from cache based on resource type
	const options = useMemo(() => {
		if (field.options_source) {
			const resourceKey = field.options_source.resource;
			return resourceOptions[resourceKey] || [];
		}
		return field.options || [];
	}, [field.options, field.options_source, resourceOptions]);

	const isLoading = field.options_source && !resourceOptions[field.options_source.resource];

	return (
		<div>
			<Select
				id={`field-${fieldKey}`}
				label={field.label}
				options={options}
				value={typeof fieldValue === "string" ? fieldValue : ""}
				onChange={(newValue) => onChange(fieldKey, newValue)}
				placeholder={isLoading ? "Loading..." : field.placeholder || `Select ${field.label.toLowerCase()}...`}
				disabled={isLoading}
			/>
			{showHelpText && field.help_text && <p className="text-xs text-tertiary mt-1.5">{field.help_text}</p>}
		</div>
	);
}

// Render a single schema field
function renderSchemaField(
	key: string,
	field: import("@/modules/wall-of-love-template").SchemaField,
	value: unknown,
	onChange: (key: string, value: unknown) => void,
	resourceOptions?: ResourceOptionsCache,
) {
	const fieldValue = value ?? "";

	// Show help text for all fields that have it
	const showHelpText = !!field.help_text;

	// Handle select fields with dynamic options_source
	if (field.type === "select" && field.options_source) {
		return <DynamicSelectField fieldKey={key} field={field} value={value} onChange={onChange} resourceOptions={resourceOptions || {}} />;
	}

	// Handle select fields with static options
	if (field.type === "select" && field.options) {
		return (
			<div>
				<Select
					id={`field-${key}`}
					label={field.label}
					options={field.options}
					value={typeof fieldValue === "string" ? fieldValue : ""}
					onChange={(newValue) => onChange(key, newValue)}
					placeholder={field.placeholder || `Select ${field.label.toLowerCase()}...`}
				/>
				{showHelpText && field.help_text && <p className="text-xs text-tertiary mt-1.5">{field.help_text}</p>}
			</div>
		);
	}

	if (field.type === "string" || field.type === "url") {
		return (
			<div>
				<Input
					id={`field-${key}`}
					label={field.label}
					type={field.type === "url" ? "url" : "text"}
					value={typeof fieldValue === "string" ? fieldValue : ""}
					onChange={(e) => onChange(key, e.target.value)}
					required={field.required}
					maxLength={field.max}
					placeholder={field.type === "url" ? "https://example.com" : `Enter ${field.label.toLowerCase()}...`}
				/>
				{showHelpText && field.help_text && <p className="text-xs text-tertiary mt-1.5">{field.help_text}</p>}
			</div>
		);
	}

	if (field.type === "number") {
		return (
			<div>
				<Input
					id={`field-${key}`}
					label={field.label}
					type="number"
					value={typeof fieldValue === "number" ? fieldValue : typeof fieldValue === "string" ? fieldValue : ""}
					onChange={(e) => {
						const numValue = e.target.value === "" ? "" : Number(e.target.value);
						onChange(key, numValue);
					}}
					required={field.required}
					min={field.min}
					max={field.max}
					placeholder={`Enter ${field.label.toLowerCase()}...`}
				/>
				{showHelpText && field.help_text && <p className="text-xs text-tertiary mt-1.5">{field.help_text}</p>}
			</div>
		);
	}

	if (field.type === "boolean") {
		return (
			<div>
				<label className="flex items-center gap-3 cursor-pointer">
					<input
						id={`field-${key}`}
						type="checkbox"
						checked={typeof fieldValue === "boolean" ? fieldValue : false}
						onChange={(e) => onChange(key, e.target.checked)}
						className="w-4 h-4 rounded border-gray-300 text-brand focus:ring-brand"
					/>
					<div className="flex-1">
						<span className="text-sm font-medium text-primary">{field.label}</span>
						{field.required && <span className="text-red-500 ml-1">*</span>}
						{showHelpText && field.help_text && <p className="text-xs text-tertiary mt-0.5">{field.help_text}</p>}
					</div>
				</label>
			</div>
		);
	}

	if (field.type === "textarea") {
		return (
			<div>
				<Textarea
					id={`field-${key}`}
					label={field.label}
					value={typeof fieldValue === "string" ? fieldValue : ""}
					onChange={(e) => onChange(key, e.target.value)}
					required={field.required}
					maxLength={field.max}
					placeholder={`Enter ${field.label.toLowerCase()}...`}
					rows={field.min || 4}
				/>
				{showHelpText && field.help_text && <p className="text-xs text-tertiary mt-1.5">{field.help_text}</p>}
			</div>
		);
	}

	return null;
}

// Dynamic Schema Fields Component (flat rendering, no grouping)
interface DynamicSchemaFieldsProps {
	schema: Record<string, import("@/modules/wall-of-love-template").SchemaField>;
	values: Record<string, unknown>;
	onChange: (key: string, value: unknown) => void;
	resourceOptions?: ResourceOptionsCache;
}

function DynamicSchemaFields({ schema, values, onChange, resourceOptions }: DynamicSchemaFieldsProps) {
	return (
		<div className="space-y-5">
			{Object.entries(schema)
				.filter(([, field]) => !field.hidden)
				.map(([key, field]) => (
					<div key={key}>{renderSchemaField(key, field, values[key], onChange, resourceOptions)}</div>
				))}
		</div>
	);
}

// Grouped Schema Fields Component (with accordion grouping)
interface GroupedSchemaFieldsProps {
	schema: Record<string, import("@/modules/wall-of-love-template").SchemaField>;
	groups?: Record<string, import("@/modules/wall-of-love-template").SchemaGroup>;
	values: Record<string, unknown>;
	onChange: (key: string, value: unknown) => void;
	resourceOptions?: ResourceOptionsCache;
	allConfigs?: { content: Record<string, unknown>; layout: Record<string, unknown>; seo?: Record<string, unknown> };
}

function GroupedSchemaFields({ schema, groups, values, onChange, resourceOptions, allConfigs }: GroupedSchemaFieldsProps) {
	// If no groups defined, fall back to flat rendering
	if (!groups || Object.keys(groups).length === 0) {
		return <DynamicSchemaFields schema={schema} values={values} onChange={onChange} resourceOptions={resourceOptions} />;
	}

	// Group fields by their group property (excluding hidden fields)
	const groupedFields: Record<string, Array<[string, import("@/modules/wall-of-love-template").SchemaField]>> = {};
	const ungroupedFields: Array<[string, import("@/modules/wall-of-love-template").SchemaField]> = [];

	for (const [key, field] of Object.entries(schema)) {
		// Skip hidden fields
		if (field.hidden) continue;

		if (field.group && groups[field.group]) {
			if (!groupedFields[field.group]) {
				groupedFields[field.group] = [];
			}
			groupedFields[field.group].push([key, field]);
		} else {
			ungroupedFields.push([key, field]);
		}
	}

	// Sort groups by their order property
	const sortedGroups = Object.entries(groups).sort(([, a], [, b]) => a.order - b.order);

	// Filter groups by visibility conditions
	const visibleGroups = sortedGroups.filter(([, group]) => {
		if (!allConfigs) return true;
		return shouldGroupBeVisible(group, allConfigs);
	});

	// Determine default open accordions (only from visible groups)
	const defaultOpenValues = visibleGroups.filter(([, group]) => group.default_open).map(([groupKey]) => groupKey);

	return (
		<div className="space-y-1">
			<Accordion type="multiple" defaultValue={defaultOpenValues}>
				{visibleGroups.map(([groupKey, group]) => {
					const fields = groupedFields[groupKey] || [];

					// Filter fields by visibility conditions
					const visibleFields = fields.filter(([, field]) => {
						if (!allConfigs) return true;
						return shouldFieldBeVisible(field, allConfigs);
					});

					if (visibleFields.length === 0) return null;

					return (
						<AccordionItem key={groupKey} value={groupKey}>
							<AccordionTrigger description={group.description}>{group.label}</AccordionTrigger>
							<AccordionContent>
								<div className="space-y-5">
									{visibleFields.map(([fieldKey, field]) => (
										<div key={fieldKey}>{renderSchemaField(fieldKey, field, values[fieldKey], onChange, resourceOptions)}</div>
									))}
								</div>
							</AccordionContent>
						</AccordionItem>
					);
				})}
			</Accordion>

			{/* Render ungrouped fields at the end */}
			{ungroupedFields.length > 0 && (
				<div className="space-y-5 pt-4">
					{ungroupedFields
						.filter(([, field]) => {
							if (!allConfigs) return true;
							return shouldFieldBeVisible(field, allConfigs);
						})
						.map(([key, field]) => (
							<div key={key}>{renderSchemaField(key, field, values[key], onChange, resourceOptions)}</div>
						))}
				</div>
			)}
		</div>
	);
}

// Save context passed to parent for sequential API calls
export interface WallOfLoveSaveContext {
	wallType: "dynamic" | "static";
	staticReviewIds: string[];
	pinnedReviewIds: string[];
	staticReviewsChanged: boolean;
	pinnedReviewsChanged: boolean;
}

interface WallOfLoveBuilderProps {
	currentBrand: Brand;
	brandUrl: string;
	brandKit: BrandKit | null;
	tags: Tag[] | null;
	wallData: WallOfLove | null;
	templateData: WallOfLoveTemplate | null;
	workspaceId: string;
	onSave: (payload: WallOfLoveUpdateRequest, context: WallOfLoveSaveContext) => Promise<void>;
}

// Builder state interface for tracking changes
interface BuilderState {
	title: string;
	type: "dynamic" | "static";
	template: string;
	template_version: number;
	content_config: Record<string, unknown>;
	layout_config: Record<string, unknown>;
	seo_config: Record<string, unknown>;
	filters: WallOfLoveFilters | null;
	sort: WallOfLoveSort | null;
	status: "published" | "unpublished";
	staticReviewIds: string[];
	pinnedReviewIds: string[];
	reviews_pinned_count?: number;
	reviews_unpinned_count?: number;
	reviews_curated_count?: number;
	reviews_pinned?: Review[];
	reviews_curated?: Review[];
}

export default function WallOfLoveBuilder({ currentBrand, brandUrl, brandKit, tags, wallData, templateData, workspaceId, onSave }: WallOfLoveBuilderProps) {
	// Tab state
	const [activeTab, setActiveTab] = useState<TabType>("content");

	// Plan and brand data for feature checks
	const { planDetails } = usePlan();
	const { selectedBrand } = useBrands();

	const allowDynamicWalls = planDetails?.plan?.features?.dynamic_walls ?? false;

	// Modal state
	const [selectReviewsModalOpen, setSelectReviewsModalOpen] = useState(false);
	const [selectPinnedReviewsModalOpen, setSelectPinnedReviewsModalOpen] = useState(false);
	const [staticSelectReviewsModalOpen, setStaticSelectReviewsModalOpen] = useState(false);
	const [modeSwitchConfirmOpen, setModeSwitchConfirmOpen] = useState(false);
	const [pendingMode, setPendingMode] = useState<WallMode | null>(null);
	const [statusChangeConfirmOpen, setStatusChangeConfirmOpen] = useState(false);
	const [publishedLinkModalOpen, setPublishedLinkModalOpen] = useState(false);
	const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
	const [planLimitError, setPlanLimitError] = useState<WallPlanLimitError | null>(null);

	// Title editing state
	const [isTitleFocused, setIsTitleFocused] = useState(false);
	const titleInputRef = useRef<HTMLInputElement>(null);

	// Track initial state from API (source of truth)
	const [initialState, setInitialState] = useState<BuilderState | null>(null);

	// Current editable state
	const [currentState, setCurrentState] = useState<BuilderState | null>(null);

	// Preview from backend API
	const { previewWall } = useWallOfLoves();
	const { indexReviewsForWallOfLove } = useReviews();
	const { forms, indexForms } = useForms();
	const [previewData, setPreviewData] = useState<WallPreviewResponse | null>(null);

	// Resource options for dynamic select fields
	const [resourceOptions, setResourceOptions] = useState<ResourceOptionsCache>({});
	const [isPreviewLoading, setIsPreviewLoading] = useState(true);
	const [isLoadingMoreReviews, setIsLoadingMoreReviews] = useState(false);
	const [accumulatedReviews, setAccumulatedReviews] = useState<Review[]>([]);
	const [wallLinkCopied, setWallLinkCopied] = useState(false);
	const [showMockReviews, setShowMockReviews] = useState(false);
	const debouncedPreviewRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const triggerPreviewRef = useRef<() => void>(() => {});

	// Saving state
	const [isSaving, setIsSaving] = useState(false);
	type SaveStatus = "idle" | "saving" | "saved" | "error";
	const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");
	const savedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	// Validation state
	const [validationErrors, setValidationErrors] = useState<WallValidationError[]>([]);

	// Customize tab state - Typography (font names for loading Google Fonts)
	const [headingFont, setHeadingFont] = useState(brandKit?.font_heading_name || "Inter");
	const [bodyFont, setBodyFont] = useState(brandKit?.font_body_name || "Inter");

	// Filter state for UI - initialized from currentState.filters (setter used for syncing)
	const [localFilters, setLocalFilters] = useState<ReviewFilters>(() => {
		// Initialize from wallData filters if available
		return wallData?.filters ? convertWallFiltersToReviewFilters(wallData.filters) : {};
	});

	// Calculate dirty state - only compare editable fields, not derived/computed data
	const isDirty = useMemo(() => {
		if (!initialState || !currentState) return false;

		return !equal(initialState, currentState);
	}, [initialState, currentState]);

	// Set up dirty state tracking and navigation guards
	useDirtyState({ hasUnsavedChanges: isDirty });

	// Navigation hooks
	const navigate = useNavigate();
	const { confirmNavigation } = useNavigationGuard();

	// Handle back button click with navigation guard
	const handleBackClick = () => {
		if (confirmNavigation()) {
			navigate(`/${brandUrl}/wall-of-love`);
		}
	};

	// Handle branding link click with navigation guard
	const handleBrandingLinkClick = () => {
		if (confirmNavigation()) {
			navigate(`/${brandUrl}/branding`);
		}
	};

	// Initialize state from wallData
	useEffect(() => {
		if (wallData) {
			const state: BuilderState = {
				title: wallData.title,
				type: wallData.type,
				template: wallData.template,
				template_version: wallData.template_version,
				content_config: wallData.content_config || {},
				layout_config: wallData.layout_config || {},
				seo_config: wallData.seo_config || {},
				filters: wallData.filters,
				sort: wallData.sort,
				status: wallData.status,
				reviews_pinned_count: wallData.reviews_pinned_count,
				reviews_unpinned_count: wallData.reviews_unpinned_count,
				reviews_pinned: wallData.reviews_pinned,
				reviews_curated_count: wallData.reviews_curated_count,
				reviews_curated: wallData?.reviews_curated || [],
				staticReviewIds: wallData.reviews_curated?.map((r) => r.id) || [],
				pinnedReviewIds: wallData.reviews_pinned?.map((r) => r.id) || [],
			};
			setInitialState(state);
			setCurrentState(state);
			// Sync local filters from wall data
			setLocalFilters(convertWallFiltersToReviewFilters(wallData.filters));
		}
	}, [wallData]);

	const wallLinkUrl = useMemo(() => {
		return `${import.meta.env.VITE_CLIENT_WALLS_URL}/w/${wallData?.slug}`;
	}, [wallData]);

	// Load resource options for dynamic select fields based on template schema
	useEffect(() => {
		if (!templateData || !workspaceId) return;

		// Collect all unique resources needed from schemas
		const requiredResources = new Set<string>();

		const scanSchemaForResources = (schema: Record<string, import("@/modules/wall-of-love-template").SchemaField> | undefined) => {
			if (!schema) return;
			for (const field of Object.values(schema)) {
				if (field.type === "select" && field.options_source?.type === "system") {
					requiredResources.add(field.options_source.resource);
				}
			}
		};

		scanSchemaForResources(templateData.content_schema);
		scanSchemaForResources(templateData.layout_schema);
		scanSchemaForResources(templateData.seo_schema);

		// Load each required resource
		const loadResources = async () => {
			for (const resource of requiredResources) {
				// Skip if already loaded
				if (resourceOptions[resource]) continue;

				if (resource === "forms") {
					const result = await indexForms(workspaceId);
					if (result.success && result.data) {
						// Find the options_source config to get label_key and value_key
						let labelKey = "title";
						let valueKey = "id";

						// Search for the field config to get the correct keys
						const findOptionsSource = (schema: Record<string, import("@/modules/wall-of-love-template").SchemaField> | undefined) => {
							if (!schema) return null;
							for (const field of Object.values(schema)) {
								if (field.type === "select" && field.options_source?.resource === resource) {
									return field.options_source;
								}
							}
							return null;
						};

						const optionsSource = findOptionsSource(templateData.content_schema) || findOptionsSource(templateData.layout_schema) || findOptionsSource(templateData.seo_schema);

						if (optionsSource) {
							labelKey = optionsSource.label_key;
							valueKey = optionsSource.value_key;
						}

						const options = result.data.map((form) => ({
							value: String(form[valueKey as keyof typeof form] || ""),
							label: String(form[labelKey as keyof typeof form] || ""),
						}));

						setResourceOptions((prev) => ({
							...prev,
							[resource]: options,
						}));
					}
				}
				// Add more resource types here as needed (e.g., "tags", "brands", etc.)
			}
		};

		loadResources();
	}, [templateData, workspaceId, indexForms]);

	// Update resourceOptions when forms change
	useEffect(() => {
		if (forms && forms.length > 0 && templateData) {
			// Find the options_source config for forms
			const findOptionsSource = (schema: Record<string, import("@/modules/wall-of-love-template").SchemaField> | undefined) => {
				if (!schema) return null;
				for (const field of Object.values(schema)) {
					if (field.type === "select" && field.options_source?.resource === "forms") {
						return field.options_source;
					}
				}
				return null;
			};

			const optionsSource = findOptionsSource(templateData.content_schema) || findOptionsSource(templateData.layout_schema) || findOptionsSource(templateData.seo_schema);

			if (optionsSource) {
				const options = forms.map((form) => ({
					value: String(form[optionsSource.value_key as keyof typeof form] || ""),
					label: String(form[optionsSource.label_key as keyof typeof form] || ""),
				}));

				setResourceOptions((prev) => ({
					...prev,
					forms: options,
				}));
			}
		}
	}, [forms, templateData]);

	// Fetch preview from backend
	const fetchPreview = useCallback(async () => {
		if (!workspaceId || !currentState) return;

		setIsPreviewLoading(true);
		// Reset accumulated reviews on fresh preview fetch
		setAccumulatedReviews([]);
		try {
			const payload: WallPreviewRequest =
				currentState.type === "dynamic"
					? {
							type: "dynamic",
							filters: currentState.filters,
							sort: currentState.sort,
							pinned_review_ids: currentState.pinnedReviewIds,
						}
					: {
							type: "static",
							review_ids: currentState.staticReviewIds,
						};

			const response = await previewWall(workspaceId, payload);
			if (response.success && response.data) {
				setPreviewData(response.data);
			}
		} finally {
			setIsPreviewLoading(false);
		}
	}, [workspaceId, currentState, previewWall]);

	// Debounced preview trigger
	const triggerPreview = useCallback(() => {
		if (debouncedPreviewRef.current) {
			clearTimeout(debouncedPreviewRef.current);
		}
		debouncedPreviewRef.current = setTimeout(() => {
			fetchPreview();
		}, PREVIEW_DEBOUNCE_MS);
	}, [fetchPreview]);

	// Keep ref updated with latest triggerPreview
	triggerPreviewRef.current = triggerPreview;

	// Cleanup debounce timer on unmount
	useEffect(() => {
		return () => {
			if (debouncedPreviewRef.current) {
				clearTimeout(debouncedPreviewRef.current);
			}
			if (savedTimeoutRef.current) {
				clearTimeout(savedTimeoutRef.current);
			}
		};
	}, []);

	// Get next cursor from preview data
	const nextCursor = useMemo(() => {
		if (!previewData) return null;
		return previewData.type === "dynamic" ? previewData.next_cursor : null;
	}, [previewData]);

	// Handle load more reviews (pagination)
	const handleLoadMore = useCallback(async () => {
		if (!workspaceId || !currentState || !nextCursor || isLoadingMoreReviews) return;

		setIsLoadingMoreReviews(true);
		try {
			// Build payload with cursor for pagination
			const payload: WallPreviewRequest =
				currentState.type === "dynamic"
					? {
							type: "dynamic",
							filters: currentState.filters,
							sort: currentState.sort,
							pinned_review_ids: currentState.pinnedReviewIds,
						}
					: {
							type: "static",
							review_ids: currentState.staticReviewIds,
						};

			// Add cursor to the request (using query param approach via modified payload)
			const response = await previewWall(workspaceId, payload, nextCursor);
			if (response.success && response.data) {
				// Append new reviews to accumulated list
				const newReviews = response.data.type === "dynamic" ? response.data.unpinned || [] : response.data.reviews || [];

				setAccumulatedReviews((prev) => {
					// If this is the first "load more", include initial page reviews
					if (prev.length === 0) {
						const initialReviews = previewData?.type === "dynamic" ? previewData.unpinned || [] : previewData?.reviews || [];
						return [...initialReviews, ...newReviews];
					}
					return [...prev, ...newReviews];
				});
				// Update preview data with new pagination info
				setPreviewData(response.data);
			}
		} finally {
			setIsLoadingMoreReviews(false);
		}
	}, [workspaceId, currentState, nextCursor, isLoadingMoreReviews, previewWall, previewData]);

	// Trigger preview when relevant state changes
	useEffect(() => {
		if (currentState) {
			triggerPreviewRef.current();
		}
	}, [currentState?.type, currentState?.filters, currentState?.sort, currentState?.pinnedReviewIds, currentState?.staticReviewIds]);

	// Merge template defaults when templateData changes
	useEffect(() => {
		if (templateData?.defaults && currentState) {
			setCurrentState((prev) => {
				if (!prev) return null;

				// Merge: start with defaults, then overlay all existing values
				// This preserves both schema-defined fields and extra fields set by the template
				const mergedContent = {
					...templateData.defaults.content,
					...prev.content_config,
				};
				const mergedLayout = {
					...templateData.defaults.layout,
					...prev.layout_config,
				};

				return {
					...prev,
					content_config: mergedContent,
					layout_config: mergedLayout,
				};
			});
		}
	}, [templateData]);

	// Validate configs when they change
	useEffect(() => {
		if (!templateData || !currentState) {
			setValidationErrors([]);
			return;
		}

		const result = validateWallOfLoveConfig(
			{
				contentConfig: currentState.content_config,
				layoutConfig: currentState.layout_config,
				seoConfig: currentState.seo_config,
			},
			{
				contentSchema: templateData.content_schema,
				layoutSchema: templateData.layout_schema,
				seoSchema: templateData.seo_schema,
			},
		);

		if (result.isValid) {
			setValidationErrors([]);
			return;
		}

		// Convert validation errors to display format
		const errors: WallValidationError[] = [];

		const addErrors = (category: "content" | "layout" | "seo", schema: Record<string, import("@/modules/wall-of-love-template").SchemaField>, errorMap: Record<string, string[]>) => {
			for (const [field, fieldErrors] of Object.entries(errorMap)) {
				const fieldSchema = schema[field];
				const fieldLabel = fieldSchema?.label || field;
				for (const message of fieldErrors) {
					errors.push({
						id: `${category}-${field}-${message}`,
						field,
						message: `${fieldLabel}: ${message}`,
						category,
						group: fieldSchema?.group,
					});
				}
			}
		};

		addErrors("content", templateData.content_schema, result.content.errors);
		addErrors("layout", templateData.layout_schema, result.layout.errors);
		if (templateData.seo_schema) {
			addErrors("seo", templateData.seo_schema, result.seo.errors);
		}

		setValidationErrors(errors);
	}, [currentState?.content_config, currentState?.layout_config, currentState?.seo_config, templateData]);

	// Sync font names with brand kit for loading Google Fonts
	useEffect(() => {
		if (brandKit) {
			if (brandKit.font_heading_name) {
				setHeadingFont(brandKit.font_heading_name);
			}
			if (brandKit.font_body_name) {
				setBodyFont(brandKit.font_body_name);
			}
		}
	}, [brandKit]);

	// Load fonts on change with specific weights from brandKit
	useEffect(() => {
		if (headingFont && brandKit?.font_heading_weight) {
			loadGoogleFont(headingFont, [brandKit.font_heading_weight]);
		}
	}, [headingFont, brandKit?.font_heading_weight]);

	useEffect(() => {
		if (bodyFont && brandKit?.font_body_weight) {
			loadGoogleFont(bodyFont, [brandKit.font_body_weight]);
		}
	}, [bodyFont, brandKit?.font_body_weight]);

	const tabs = [
		{
			id: "content" as const,
			label: "Content",
			icon: (
				<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth={2}
						d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
					/>
				</svg>
			),
		},
		{
			id: "design" as const,
			label: "Design",
			icon: (
				<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth={2}
						d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01"
					/>
				</svg>
			),
		},
	];

	// Wall mode from type field
	const wallMode: WallMode = currentState?.type || "dynamic";

	// Handle wall mode change - show confirmation dialog
	const handleWallModeChange = (mode: WallMode) => {
		if (mode !== wallMode) {
			setPendingMode(mode);
			setModeSwitchConfirmOpen(true);
		}
	};

	// Confirm mode switch
	const confirmModeSwitch = async () => {
		if (!pendingMode) return;

		if (pendingMode === "static") {
			// Initialize static wall - restore staticReviewIds from initialState if available, otherwise from wallData
			const restoredStaticIds = initialState?.staticReviewIds && initialState.staticReviewIds.length > 0 ? initialState.staticReviewIds : wallData?.reviews_curated?.map((r) => r.id) || [];
			const restoredStaticReviews = initialState?.reviews_curated && initialState.reviews_curated.length > 0 ? initialState.reviews_curated : wallData?.reviews_curated || [];
			console.log("review count", initialState?.reviews_curated_count, wallData?.reviews_curated_count);
			const restoredReviewCount = initialState?.reviews_curated_count || wallData?.reviews_curated_count || 0;

			setCurrentState((prev) => {
				if (!prev) return null;
				return {
					...prev,
					type: "static",
					staticReviewIds: restoredStaticIds,
					reviews_curated: restoredStaticReviews,
					reviews_curated_count: restoredReviewCount,
					pinnedReviewIds: [],
					filters: null,
					sort: null,
				};
			});
		} else {
			// Reset to dynamic mode - restore filters and sort from initialState
			const restoredFilters = initialState?.filters || null;
			const restoredSort = initialState?.sort || [
				{
					field: "date",
					direction: "desc" as const,
				},
			];
			const restoredPinnedIds = initialState?.pinnedReviewIds || [];

			// Fetch the actual count based on restored filters using review search endpoint
			let unpinnedCount = 0;
			if (restoredFilters) {
				try {
					const reviewFilters = convertWallFiltersToReviewFilters(restoredFilters);
					const reviewSort = convertWallSortToReviewSort(restoredSort);
					const response = await indexReviewsForWallOfLove(workspaceId, null, reviewFilters, reviewSort, restoredPinnedIds);
					if (response?.meta?.total !== undefined) {
						unpinnedCount = response.meta.total;
					}
				} catch (error) {
					console.error("Failed to fetch review count:", error);
				}
			}

			setCurrentState((prev) => {
				if (!prev) return null;
				return {
					...prev,
					type: "dynamic",
					staticReviewIds: [],
					pinnedReviewIds: restoredPinnedIds,
					filters: restoredFilters,
					sort: restoredSort,
					reviews_pinned_count: initialState?.reviews_pinned_count,
					reviews_unpinned_count: unpinnedCount,
					reviews_pinned: initialState?.reviews_pinned,
				};
			});
			// Also restore local filters
			if (restoredFilters) {
				setLocalFilters(convertWallFiltersToReviewFilters(restoredFilters));
			} else {
				setLocalFilters({});
			}
		}

		setModeSwitchConfirmOpen(false);
		setPendingMode(null);
	};

	// Handle reviews save from modal
	const handleReviewsSave = (newFilters: WallOfLoveFilters | null, newSort: WallOfLoveSort | null, totalMatchingCount: number) => {
		setCurrentState((prev) => {
			if (!prev) return null;
			return {
				...prev,
				filters: newFilters,
				sort: newSort,
				reviews_unpinned_count: totalMatchingCount,
			};
		});
		// Also update local filters
		setLocalFilters(convertWallFiltersToReviewFilters(newFilters));
	};

	// Handle pinned reviews save from modal
	const handlePinnedReviewsSave = (pinnedIds: string[], totalMatchingCount: number) => {
		setCurrentState((prev) => {
			if (!prev) return null;
			return {
				...prev,
				pinnedReviewIds: pinnedIds,
				reviews_unpinned_count: totalMatchingCount,
			};
		});
	};

	// Handle static reviews save from modal
	const handleStaticReviewsSave = (selectedIds: string[], selectedReviews: Review[]) => {
		setCurrentState((prev) => {
			if (!prev) return null;
			return {
				...prev,
				staticReviewIds: selectedIds,
				reviews: selectedReviews,
			};
		});
	};

	// Get pinned review IDs (for dynamic mode)
	const pinnedReviewIds = currentState?.pinnedReviewIds || [];

	// Get static review IDs (for static mode)
	const staticReviewIds = currentState?.staticReviewIds || [];

	// Derive preview reviews from backend response (pure render - no client-side computation)
	// Combines initial page reviews with accumulated reviews from "load more"
	const previewReviews = useMemo((): Review[] => {
		if (!previewData) return [];

		if (previewData.type === "dynamic") {
			// Dynamic mode: pinned reviews first, then unpinned reviews
			const pinned = previewData.pinned || [];
			const unpinned = previewData.unpinned || [];
			// If we have accumulated reviews from pagination, only use pinned + accumulated
			// Otherwise use the initial page (pinned + unpinned)
			if (accumulatedReviews.length > 0) {
				return [...pinned, ...accumulatedReviews];
			}
			return [...pinned, ...unpinned];
		}

		// Static mode: exactly the selected reviews in order
		const reviews = previewData.reviews || [];
		if (accumulatedReviews.length > 0) {
			return accumulatedReviews;
		}
		return reviews;
	}, [previewData, accumulatedReviews]);
	const mockReviews = useMemo(() => getMockReviews(currentBrand?.name), [currentBrand?.name]);
	const hasPreviewReviews = previewReviews.length > 0;
	const displayedReviews = showMockReviews ? mockReviews : previewReviews;

	// Get total matching count from preview metadata
	const totalMatchingCount = useMemo(() => {
		if (!currentState?.reviews_unpinned_count) return 0;
		// Both dynamic and static have selected_count in meta
		return currentState.reviews_unpinned_count ?? 0;
	}, [currentState]);

	useEffect(() => {
		if (hasPreviewReviews) {
			setShowMockReviews(false);
		}
	}, [hasPreviewReviews]);

	// Update content config field
	const handleCopyLink = async () => {
		await copyToClipboard(wallLinkUrl);
		setWallLinkCopied(true);
		setTimeout(() => {
			setWallLinkCopied(false);
		}, 2000);
	};

	const handleContentConfigUpdate = (key: string, value: unknown) => {
		setCurrentState((prev) => {
			if (!prev) return null;
			return {
				...prev,
				content_config: { ...prev.content_config, [key]: value },
			};
		});
	};

	// Update layout config field
	const handleLayoutConfigUpdate = (key: string, value: unknown) => {
		setCurrentState((prev) => {
			if (!prev) return null;
			return {
				...prev,
				layout_config: { ...prev.layout_config, [key]: value },
			};
		});
	};

	// Handle title change
	const handleTitleChange = (title: string) => {
		setCurrentState((prev) => {
			if (!prev) return null;
			return { ...prev, title };
		});
	};

	// Handle status change
	const handleStatusChange = (status: string) => {
		setCurrentState((prev) => {
			if (!prev) return null;
			return { ...prev, status: status as "published" | "unpublished" };
		});
	};

	const handleContentConfigChange = (key: string, value: unknown) => {
		setCurrentState((prev) => {
			if (!prev) return null;
			return {
				...prev,
				content_config: { ...prev.content_config, [key]: value },
			};
		});
	};

	// Check if status is changing from published to another status
	const isStatusChangingFromPublished = initialState?.status === "published" && currentState?.status !== "published";
	// Check if status is changing to published from another status
	const isStatusChangingToPublished = initialState?.status !== "published" && currentState?.status === "published";

	// Handle save button click - show confirmation if changing from/to published
	const handleSaveClick = () => {
		if (isStatusChangingFromPublished || isStatusChangingToPublished) {
			setStatusChangeConfirmOpen(true);
		} else {
			handleSave();
		}
	};

	// Handle save
	const handleSave = async () => {
		if (!isDirty || !currentState || !initialState) return;

		// Check if status is changing TO "published"
		const isChangingToPublished = initialState.status !== "published" && currentState.status === "published";

		// Clear any existing timeout
		if (savedTimeoutRef.current) {
			clearTimeout(savedTimeoutRef.current);
		}

		setIsSaving(true);
		setSaveStatus("saving");
		try {
			// Build partial update payload (only changed fields)
			const payload: WallOfLoveUpdateRequest = {};

			if (currentState.title !== initialState.title) {
				payload.title = currentState.title;
			}
			if (currentState.type !== initialState.type) {
				payload.type = currentState.type;
			}
			if (currentState.template !== initialState.template) {
				payload.template = currentState.template;
			}
			if (currentState.template_version !== initialState.template_version) {
				payload.template_version = currentState.template_version;
			}
			if (currentState.status !== initialState.status) {
				payload.status = currentState.status;
			}
			if (JSON.stringify(currentState.content_config) !== JSON.stringify(initialState.content_config)) {
				// Send all content config values (schema validation happens on backend)
				payload.content_config = currentState.content_config;
			}
			if (JSON.stringify(currentState.layout_config) !== JSON.stringify(initialState.layout_config)) {
				payload.layout_config = Object.fromEntries(Object.entries(currentState.layout_config).filter(([key]) => (templateData?.layout_schema ? key in templateData.layout_schema : true)));
			}
			if (!equal(currentState.seo_config, initialState.seo_config)) {
				payload.seo_config = currentState.seo_config;
			}
			// Only include filters for dynamic walls
			if (currentState.type === "dynamic" && !equal(currentState.filters, initialState.filters)) {
				payload.filters = currentState.filters;
			}
			if (JSON.stringify(currentState.sort) !== JSON.stringify(initialState.sort)) {
				payload.sort = currentState.sort;
			}

			// Detect review changes for sync
			const staticReviewsChanged = JSON.stringify(currentState.staticReviewIds) !== JSON.stringify(initialState.staticReviewIds);
			const pinnedReviewsChanged = JSON.stringify(currentState.pinnedReviewIds) !== JSON.stringify(initialState.pinnedReviewIds);

			// Call onSave with context for sequential API calls
			await onSave(payload, {
				wallType: currentState.type,
				staticReviewIds: currentState.staticReviewIds,
				pinnedReviewIds: currentState.pinnedReviewIds,
				staticReviewsChanged,
				pinnedReviewsChanged,
			});

			// Close the status change confirmation modal if open
			setStatusChangeConfirmOpen(false);

			// Update initial state to current (reset dirty)
			setInitialState(currentState);
			setSaveStatus("saved");

			// Hide "Saved" after 2 seconds
			savedTimeoutRef.current = setTimeout(() => {
				setSaveStatus("idle");
			}, 2000);

			// If status changed to published, show the link modal
			if (isChangingToPublished) {
				// Generate a random link for now
				setPublishedLinkModalOpen(true);
			}
		} catch (error) {
			console.error("Failed to save wall of love:", error);

			// Check if it's a plan limit error
			if (error instanceof WallPlanLimitError) {
				setPlanLimitError(error);
				setUpgradeModalOpen(true);
				setStatusChangeConfirmOpen(false);
				setSaveStatus("idle");
			} else {
				setSaveStatus("error");
				setStatusChangeConfirmOpen(false);
				// Reset error status after 3 seconds
				savedTimeoutRef.current = setTimeout(() => {
					setSaveStatus("idle");
				}, 3000);
			}
		} finally {
			setIsSaving(false);
		}
	};

	// Handle discard
	const handleDiscard = () => {
		if (initialState) {
			// Create a deep copy to ensure React detects the state change for all nested objects
			setCurrentState(structuredClone(initialState));
			// Reset local filters to initial state
			setLocalFilters(convertWallFiltersToReviewFilters(initialState.filters));
		}
	};

	// Show 404 message if wall not found
	if (!wallData) {
		return (
			<div className="flex items-center justify-center min-h-[calc(100vh-4rem)] bg-[#fafafa]">
				<div className="text-center max-w-full px-6">
					<div className="w-16 h-16 mx-auto mb-6 rounded-full bg-gray-100 flex items-center justify-center">
						<svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
							/>
						</svg>
					</div>
					<h2 className="text-xl font-semibold text-primary mb-2">Wall Not Found</h2>
					<p className="text-tertiary mb-6">
						This wall doesn't exist or has been deleted.
						<br />
						Please select another wall or create a new one.
					</p>
					<button
						type="button"
						onClick={() => navigate(`/${brandUrl}/wall-of-love`)}
						className="px-4 py-2 text-sm font-medium rounded-lg transition-colors cursor-pointer bg-brand text-white hover:bg-brand/90 focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-1"
					>
						Go to Wall of Love
					</button>
				</div>
			</div>
		);
	}

	// Show blocked message if wall is blocked by plan
	if (wallData.blocked_by_plan) {
		return (
			<div className="flex items-center justify-center min-h-[calc(100vh-4rem)] bg-[#fafafa]">
				<div className="text-center max-w-full px-6">
					<div className="w-16 h-16 mx-auto mb-6 rounded-full bg-gray-100 flex items-center justify-center">
						<svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
							<path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
						</svg>
					</div>
					<h2 className="text-xl font-semibold text-primary mb-2">Feature Unavailable</h2>
					<p className="text-tertiary mb-6">
						This wall is not available for your current plan.
						<br />
						Please upgrade to enable it.
					</p>
					<button
						type="button"
						onClick={() => navigate(`/${brandUrl}/billing`)}
						className="px-4 py-2 text-sm font-medium rounded-lg transition-colors cursor-pointer bg-brand text-white hover:bg-brand/90 focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-1"
					>
						Upgrade Now
					</button>
				</div>
			</div>
		);
	}

	return (
		<div className="flex h-screen bg-[#fafafa]">
			{/* Left Column - Sidebar Panel */}
			<div className="w-100 shrink-0 bg-white border-r border-gray-200 flex flex-col h-screen">
				{/* Sidebar Header */}
				<div className="px-6 py-4 border-b border-gray-200">
					<div className="flex items-center gap-3">
						<Button onClick={handleBackClick} variant="soft" className="p-2">
							<IconArrowLeftLane className="w-5 h-5" />
						</Button>
						<div className="flex-1 min-w-0">
							{isTitleFocused ? (
								<input
									ref={titleInputRef}
									type="text"
									value={currentState?.title || ""}
									onChange={(e) => handleTitleChange(e.target.value)}
									onBlur={() => setIsTitleFocused(false)}
									autoComplete="off"
									data-1p-ignore
									data-lpignore="true"
									data-bwignore
									data-form-type="other"
									name="wall-title"
									className="font-heading text-base font-semibold bg-transparent border-none outline-none rounded px-1 -ml-1 w-full text-gray-900 hover:outline-1 hover:outline-gray-300 focus:outline-1 focus:outline-gray-400"
								/>
							) : (
								<h1
									onClick={() => {
										setIsTitleFocused(true);
										setTimeout(() => titleInputRef.current?.focus(), 0);
									}}
									className="text-base font-semibold truncate cursor-text rounded px-1 -ml-1 w-full text-gray-900 hover:outline-1 hover:outline-gray-300 focus:outline-1 focus:outline-gray-400"
								>
									{currentState?.title || "Wall of Love"}
								</h1>
							)}
						</div>
					</div>
				</div>

				{/* Tabs */}
				<div className="flex border-b border-gray-200">
					{tabs.map((tab) => (
						<button
							key={tab.id}
							type="button"
							onClick={() => setActiveTab(tab.id)}
							className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-colors relative ${
								activeTab === tab.id ? "text-gray-900" : "text-gray-500 hover:text-gray-700"
							}`}
						>
							{tab.icon}
							{tab.label}
							{activeTab === tab.id && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gray-900" />}
						</button>
					))}
				</div>

				{/* Tab Content - Scrollable */}
				<div className="flex-1 overflow-y-auto" style={{ scrollPadding: "1.5rem" }}>
					{/* Content Tab */}
					{activeTab === "content" && (
						<div className={`divide-y divide-gray-100 ${showMockReviews ? "pointer-events-none opacity-60" : ""}`}>
							{/* Wall Mode Section */}
							<div className="p-6">
								<h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
									<span className="text-gray-400">
										<IconDashboardGauge />
									</span>
									Wall Mode
								</h3>
								<WallModeSelector value={wallMode} onChange={handleWallModeChange} dynamicDisabled={!allowDynamicWalls} onUpgradeClick={() => setUpgradeModalOpen(true)} />
							</div>

							{/* Select Reviews Section */}
							<div className="p-6">
								<h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
									<span className="text-gray-400">
										<IconChatBubbleSquare />
									</span>
									Select Reviews
								</h3>

								{/* Select Reviews - Only for Dynamic mode */}
								{wallMode === "dynamic" && (
									<div className="space-y-4">
										<ReviewPinnedSelectionSummary pinnedCount={currentState?.pinnedReviewIds.length || 0} onClick={() => setSelectPinnedReviewsModalOpen(true)} />
										<ReviewSelectionSummary
											matchingCount={totalMatchingCount}
											activeFilters={currentState?.filters ?? null}
											activeSort={currentState?.sort}
											onClick={() => setSelectReviewsModalOpen(true)}
										/>
									</div>
								)}

								{/* Select Reviews - Only for Static mode */}
								{wallMode === "static" && <StaticReviewSelectionSummary selectedCount={staticReviewIds.length} onClick={() => setStaticSelectReviewsModalOpen(true)} />}
							</div>
						</div>
					)}

					{/* Design Tab */}
					{activeTab === "design" && (
						<div className="divide-y divide-gray-100">
							{/* Layout Configuration Section */}
							{templateData?.layout_schema && Object.keys(templateData.layout_schema).length > 0 && (
								<div className="py-6">
									<h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2 px-6">
										<span className="text-gray-400">
											<IconAlignTop />
										</span>
										Layout
									</h3>
									<GroupedSchemaFields
										schema={templateData.layout_schema}
										groups={templateData.layout_groups}
										values={currentState?.layout_config || {}}
										onChange={handleLayoutConfigUpdate}
										resourceOptions={resourceOptions}
										allConfigs={{
											content: currentState?.content_config || {},
											layout: currentState?.layout_config || {},
											seo: currentState?.seo_config || {},
										}}
									/>
								</div>
							)}

							{/* Content Configuration Section */}
							{templateData?.content_schema && Object.keys(templateData.content_schema).length > 0 && (
								<div className="py-6">
									<h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2 px-6">
										<IconText className="w-5 h-5 text-gray-400" />
										Content
									</h3>
									<GroupedSchemaFields
										schema={templateData.content_schema}
										groups={templateData.content_groups}
										values={currentState?.content_config || {}}
										onChange={handleContentConfigUpdate}
										resourceOptions={resourceOptions}
										allConfigs={{
											content: currentState?.content_config || {},
											layout: currentState?.layout_config || {},
											seo: currentState?.seo_config || {},
										}}
									/>
								</div>
							)}

							{/* SEO Section */}
							<div className="p-6">
								<h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
									<span className="text-gray-400">
										<IconSearchCategory />
									</span>
									SEO
								</h3>
								<div className="space-y-5 pt-4">
									<div>
										<Input
											id="field-seo_title"
											label="SEO Title"
											placeholder="Enter your SEO title..."
											value={(currentState?.seo_config?.seo_title as string) || ""}
											onChange={(e) =>
												setCurrentState((prev) => {
													if (!prev) return prev;
													return {
														...prev,
														seo_config: { ...prev.seo_config, seo_title: e.target.value },
													};
												})
											}
										/>
										{(templateData?.seo_schema?.seo_title?.help_text as string) && (
											<p className="text-xs text-tertiary mt-1.5">{templateData?.seo_schema?.seo_title?.help_text as string}</p>
										)}
									</div>
									<div>
										<Textarea
											id="field-seo_description"
											label="SEO Description"
											placeholder="Enter your SEO description..."
											value={(currentState?.seo_config?.seo_description as string) || ""}
											rows={4}
											onChange={(e) =>
												setCurrentState((prev) => {
													if (!prev) return prev;
													return {
														...prev,
														seo_config: { ...prev.seo_config, seo_description: e.target.value },
													};
												})
											}
										/>
										{(templateData?.seo_schema?.seo_description?.help_text as string) && (
											<p className="text-xs text-tertiary mt-1.5">{templateData?.seo_schema?.seo_description?.help_text as string}</p>
										)}
									</div>
								</div>
							</div>

							{/* Branding Section - Compact Data List */}
							<div className="p-6">
								<div className="flex items-center justify-between mb-4">
									<h3 className="font-semibold text-gray-900 flex items-center gap-2">
										<span className="text-gray-400">
											<IconColorSwatches />
										</span>
										Branding
									</h3>
									<button onClick={handleBrandingLinkClick} className="text-sm text-brand hover:underline font-medium">
										Edit
									</button>
								</div>
								<p className="text-sm text-tertiary mb-4">Colors, fonts, and logos are inherited from your branding settings.</p>
								<div className="bg-gray-50 rounded-xl p-4 space-y-3">
									{/* Colors Row */}
									<div className="flex items-center justify-between">
										<span className="text-sm text-tertiary">Colors</span>
										<div className="flex items-center gap-2">
											<div
												className="w-5 h-5 rounded-full border border-gray-200 shadow-sm"
												style={{ backgroundColor: brandKit?.color_primary_hex || "#a855f7" }}
												title={`Primary: ${brandKit?.color_primary_hex || "#a855f7"}`}
											/>
											<div
												className="w-5 h-5 rounded-full border border-gray-200 shadow-sm"
												style={{ backgroundColor: brandKit?.color_accent_hex || "#3b82f6" }}
												title={`Accent: ${brandKit?.color_accent_hex || "#3b82f6"}`}
											/>
										</div>
									</div>
									{/* Heading Font Row */}
									<div className="flex items-center justify-between">
										<span className="text-sm text-tertiary">Heading</span>
										<span className="text-sm text-primary font-medium">
											{brandKit?.font_heading_name || "Inter"} ({getFontWeightLabel(brandKit?.font_heading_weight || "700")})
										</span>
									</div>
									{/* Body Font Row */}
									<div className="flex items-center justify-between">
										<span className="text-sm text-tertiary">Body</span>
										<span className="text-sm text-primary font-medium">
											{brandKit?.font_body_name || "Inter"} ({getFontWeightLabel(brandKit?.font_body_weight || "400")})
										</span>
									</div>
									{/* Logo Row */}
									<div className="flex items-center justify-between">
										<span className="text-sm text-tertiary">Logo</span>
										<div className="flex items-center gap-2">
											{brandKit?.logo_icon_only_url ? (
												<img src={brandKit.logo_icon_only_url} alt="Logo" className="h-5 w-5 object-contain" />
											) : (
												<div className="h-5 w-5 rounded bg-gray-200 flex items-center justify-center">
													<svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
														<path
															strokeLinecap="round"
															strokeLinejoin="round"
															strokeWidth="2"
															d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
														/>
													</svg>
												</div>
											)}
											<span className="text-sm text-primary">
												{brandKit?.logo_icon_only_url || brandKit?.logo_icon_with_label_url || brandKit?.logo_label_only_url ? "Configured" : "Not set"}
											</span>
										</div>
									</div>
								</div>
							</div>
						</div>
					)}
				</div>
			</div>

			{/* Sticky header with Status - Offset by sidebar width */}
			<div className="fixed top-0 left-100 right-0 z-20 px-8 py-4 bg-white border-b border-gray-200">
				<div className="max-w-6xl mx-auto px-8">
					<div className="flex items-center justify-between gap-4">
						<p className="text-sm text-tertiary">
							{currentState?.status !== initialState?.status
								? currentState?.status === "published"
									? "Your wall will be published after saving."
									: "Your wall will be unpublished after saving."
								: currentState?.status === "published"
									? "Your wall is live and visible to everyone."
									: "Your wall is unpublished and hidden from public view."}
						</p>
						<div className="flex items-center gap-3">
							{/* Status dropdown */}
							<DropdownMenu.Root>
								<DropdownMenu.Trigger asChild>
									<button
										type="button"
										className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg cursor-pointer transition-colors hover:bg-gray-100 border border-gray-200 ${
											currentState?.status === "published" ? "text-green-700" : "text-gray-600"
										}`}
									>
										<span className={`w-2 h-2 rounded-full ${currentState?.status === "published" ? "bg-green-500" : "bg-gray-400"}`} />
										{currentState?.status === "published" ? "Published" : "Unpublished"}
										<svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
										</svg>
									</button>
								</DropdownMenu.Trigger>
								<DropdownMenu.Portal>
									<DropdownMenu.Content className="bg-white rounded-lg shadow-lg border border-gray-200 py-1.5 z-50 min-w-[140px]" sideOffset={5}>
										<DropdownMenu.Item
											className="flex items-center justify-between px-4 py-2.5 text-sm cursor-pointer outline-none hover:bg-gray-50"
											onSelect={() => handleStatusChange("published")}
										>
											<span className="text-gray-700">Published</span>
											{currentState?.status === "published" && (
												<svg className="w-4 h-4 text-brand" fill="none" stroke="currentColor" viewBox="0 0 24 24">
													<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
												</svg>
											)}
										</DropdownMenu.Item>
										<DropdownMenu.Item
											className="flex items-center justify-between px-4 py-2.5 text-sm cursor-pointer outline-none hover:bg-gray-50"
											onSelect={() => handleStatusChange("unpublished")}
										>
											<span className="text-gray-700">Unpublished</span>
											{currentState?.status === "unpublished" && (
												<svg className="w-4 h-4 text-brand" fill="none" stroke="currentColor" viewBox="0 0 24 24">
													<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
												</svg>
											)}
										</DropdownMenu.Item>
									</DropdownMenu.Content>
								</DropdownMenu.Portal>
							</DropdownMenu.Root>
							{currentState?.status !== initialState?.status || initialState?.status !== "published" ? (
								<Tooltip content="Link is only available when the form is published.">
									<span>
										<Button variant="light" className="flex items-center gap-2 shrink-0" disabled>
											<IconLinkChain />
											Copy Link
										</Button>
									</span>
								</Tooltip>
							) : (
								<Button variant="light" className="flex items-center justify-center gap-2 w-32 shrink-0" onClick={handleCopyLink}>
									{wallLinkCopied ? (
										<>
											<svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
											</svg>
											<span className="text-green-600">Copied!</span>
										</>
									) : (
										<>
											<IconLinkChain />
											<span>Copy Link</span>
										</>
									)}
								</Button>
							)}
						</div>
					</div>
				</div>
			</div>

			{/* Right Column - Live Preview (Full Page Content) */}
			<div className="flex-1 overflow-auto pt-18 pb-18">
				{!showMockReviews && !isPreviewLoading && !hasPreviewReviews && (
					<div className="max-w-3xl mx-auto px-8 pt-8 sticky top-0 z-20">
						<div className="rounded-xl border border-gray-200 bg-gray-50 p-4 shadow-sm">
							<div className="flex items-center justify-between gap-2">
								<div className="flex gap-4">
									<div>
										<h2 className="text-base font-semibold text-gray-800 mb-1">No Reviews Yet</h2>
										<p className="text-sm text-gray-700 max-w-xl">Curious how your wall will look? Preview it with sample reviews.</p>
									</div>
								</div>
								<Button type="button" aria-checked={showMockReviews} aria-label="Toggle preview mode" onClick={() => setShowMockReviews((prev) => !prev)} variant="light">
									Show Sample Reviews
								</Button>
							</div>
						</div>
					</div>
				)}
				{showMockReviews && (
					<div className="max-w-3xl mx-auto px-8 pt-8 sticky top-0 z-20">
						<div className="rounded-xl border border-blue-200 bg-blue-50 p-4 shadow-sm">
							<div className="flex items-center justify-between gap-4">
								<div className="flex items-start gap-4">
									<div>
										<h3 className="text-base font-semibold text-blue-900 mb-1">Previewing Sample Reviews</h3>
										<p className="text-sm text-blue-800 max-w-xl">These are sample reviews and not visible to your visitors.</p>
									</div>
								</div>
								<Button type="button" aria-checked={showMockReviews} aria-label="Toggle preview mode" onClick={() => setShowMockReviews((prev) => !prev)} variant="light">
									Hide Sample Reviews
								</Button>
							</div>
						</div>
					</div>
				)}
				{/* Preview Content - Using TemplateRouter */}
				{brandKit ? (
					<TemplateRouter
						currentBrand={currentBrand}
						template={currentState?.template || "default"}
						reviews={displayedReviews || []}
						brandKit={brandKit}
						contentConfig={currentState?.content_config}
						layoutConfig={currentState?.layout_config}
						contentSchema={templateData?.content_schema}
						onContentConfigChange={handleContentConfigChange}
						isLoading={showMockReviews ? false : isPreviewLoading}
						nextCursor={showMockReviews ? null : nextCursor}
						onLoadMore={handleLoadMore}
						isLoadingMoreReviews={showMockReviews ? false : isLoadingMoreReviews}
						resourceOptions={resourceOptions}
					/>
				) : (
					<div className="flex items-center justify-center min-h-[calc(100vh-49px)]">
						<div className="w-8 h-8 rounded-full border-2 border-gray-400 border-t-transparent animate-spin" />
					</div>
				)}
			</div>

			{/* Select Reviews Modal (Dynamic mode) */}
			<SelectReviewsModal
				open={selectReviewsModalOpen}
				onOpenChange={setSelectReviewsModalOpen}
				workspaceId={workspaceId}
				initialFilters={currentState?.filters ?? null}
				initialSort={currentState?.sort ?? null}
				pinnedIds={pinnedReviewIds}
				tags={tags ?? []}
				onSave={handleReviewsSave}
			/>

			{/* Select Pinned Reviews Modal (Dynamic mode) */}
			<SelectPinnedReviewsModal
				open={selectPinnedReviewsModalOpen}
				onOpenChange={setSelectPinnedReviewsModalOpen}
				workspaceId={workspaceId}
				wallId={wallData?.id ?? null}
				dynamicWallFilters={localFilters}
				initialPinnedIds={pinnedReviewIds}
				tags={tags ?? []}
				onSave={handlePinnedReviewsSave}
			/>

			{/* Static Select Reviews Modal (Static mode) */}
			<SelectStaticReviewsModal
				open={staticSelectReviewsModalOpen}
				onOpenChange={setStaticSelectReviewsModalOpen}
				workspaceId={workspaceId}
				initialSelectedIds={staticReviewIds}
				tags={tags ?? []}
				maxReviews={planDetails?.limits?.max_reviews_per_wall}
				userRole={selectedBrand?.role}
				onSave={handleStaticReviewsSave}
			/>

			{/* Mode Switch Confirmation Modal */}
			<ModeSwitchConfirmationModal
				open={modeSwitchConfirmOpen}
				onOpenChange={setModeSwitchConfirmOpen}
				fromMode={wallMode}
				toMode={pendingMode || wallMode}
				currentReviewCount={previewReviews.length}
				onConfirm={confirmModeSwitch}
			/>

			{/* Status Change Confirmation Modal */}
			<StatusChangeConfirmationModal
				open={statusChangeConfirmOpen}
				onOpenChange={setStatusChangeConfirmOpen}
				currentStatus={initialState?.status || "unpublished"}
				newStatus={currentState?.status || "unpublished"}
				onConfirm={handleSave}
				isLoading={isSaving}
			/>

			{/* Published Link Modal */}
			<PublishedLinkModal open={publishedLinkModalOpen} onOpenChange={setPublishedLinkModalOpen} wallTitle={currentState?.title || "Untitled"} link={wallLinkUrl} />

			{/* Upgrade to Growth Modal */}
			<UpgradeToGrowthModal
				open={upgradeModalOpen}
				onOpenChange={(open) => {
					setUpgradeModalOpen(open);
					if (!open) setPlanLimitError(null);
				}}
				context={planLimitError ? "reviews_per_wall_limit" : "dynamic_walls"}
				reviewLimit={planLimitError ? planDetails?.limits?.max_reviews_per_wall : undefined}
				workspaceId={workspaceId}
				userRole={selectedBrand?.role}
			/>

			{/* Sticky footer with Save/Discard buttons - Offset by sidebar width */}
			<div className="fixed bottom-0 left-100 right-0 z-20 px-8 py-4 bg-white border-t border-gray-200">
				<div className="max-w-6xl mx-auto px-8">
					<div className="flex items-center justify-end">
						<div className="flex items-center gap-3">
							{/* Validation errors tooltip */}
							{validationErrors.length > 0 && (
								<ValidationErrorsTooltip
									errors={validationErrors}
									onErrorClick={(error) => {
										// All schema fields (content, layout, seo) are in the design tab
										setActiveTab("design");
										// Focus the field after a short delay to allow tab switch and DOM update
										setTimeout(() => {
											// If field has a group, find and open the accordion first
											if (error.group) {
												const accordionItem = document.querySelector(`[data-accordion-value="${error.group}"]`);
												if (accordionItem && accordionItem.getAttribute("data-state") === "closed") {
													const trigger = accordionItem.querySelector("button");
													if (trigger) {
														trigger.click();
														// Wait for accordion to expand before focusing field
														setTimeout(() => {
															const fieldElement = document.getElementById(`field-${error.field}`);
															if (fieldElement) {
																fieldElement.scrollIntoView({ behavior: "smooth", block: "center" });
																fieldElement.focus();
																// If it's a select field (button element), click it to open the dropdown
																if (fieldElement.tagName === "BUTTON") {
																	setTimeout(() => {
																		fieldElement.click();
																	}, 100);
																}
															}
														}, 150);
														return;
													}
												}
											}
											// Field is not in accordion or accordion is already open
											const fieldElement = document.getElementById(`field-${error.field}`);
											if (fieldElement) {
												fieldElement.scrollIntoView({ behavior: "smooth", block: "center" });
												fieldElement.focus();
												// If it's a select field (button element), click it to open the dropdown
												if (fieldElement.tagName === "BUTTON") {
													setTimeout(() => {
														fieldElement.click();
													}, 100);
												}
											}
										}, 100);
									}}
								/>
							)}

							{/* Status indicator */}
							{saveStatus !== "idle" && (
								<span
									className={`text-sm font-medium flex items-center gap-1.5 transition-opacity duration-200 ${
										saveStatus === "saving" ? "text-gray-500" : saveStatus === "saved" ? "text-green-600" : "text-red-600"
									}`}
								>
									{saveStatus === "saving" ? (
										<>
											<svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
												<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
												<path
													className="opacity-75"
													fill="currentColor"
													d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
												/>
											</svg>
											Saving...
										</>
									) : saveStatus === "saved" ? (
										<>
											<svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
												<path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
											</svg>
											Saved
										</>
									) : (
										<>
											<svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
												<path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
											</svg>
											Error saving
										</>
									)}
								</span>
							)}

							{/* Discard button */}
							<Button variant="light" onClick={handleDiscard} disabled={showMockReviews || !isDirty || saveStatus === "saving" || saveStatus === "saved"}>
								Discard
							</Button>

							{/* Save button */}
							{showMockReviews ? (
								<Tooltip content="Exit preview mode to save your changes.">
									<span>
										<Button variant="default" onClick={handleSaveClick} disabled>
											{saveStatus === "saving" ? "Saving..." : "Save Changes"}
										</Button>
									</span>
								</Tooltip>
							) : (
								<Button variant="default" onClick={handleSaveClick} disabled={!isDirty || isSaving || validationErrors.length > 0}>
									{saveStatus === "saving" ? "Saving..." : "Save Changes"}
								</Button>
							)}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
